import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';
import type { SelectOption } from '../Select';

export interface SearchableSelectProps {
  readonly options: readonly SelectOption[];
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly emptyText?: string;
  readonly invalid?: boolean;
  readonly disabled?: boolean;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly id?: string;
  readonly 'aria-label'?: string;
  readonly 'data-testid'?: string;
}

const SIZE = {
  sm: 'h-[34px] text-[12.5px] ps-3 pe-2',
  md: 'h-10 text-[13.5px] ps-3.5 pe-3',
  lg: 'h-12 text-[15px] ps-4 pe-3.5',
};

/**
 * Single-select combobox: a Select-styled control that opens a popover with a
 * type-to-filter search box + filtered option list. Use for large single-choice
 * lists (e.g. the 67-sport catalog) where a native <Select> is unwieldy.
 *
 * The popover renders through a portal with fixed positioning so it is never
 * clipped by an overflow-hidden/auto ancestor (e.g. a Modal body).
 */
export const SearchableSelect = forwardRef<HTMLButtonElement, SearchableSelectProps>(
  function SearchableSelect(
    {
      options,
      value,
      onChange,
      placeholder = 'Select…',
      searchPlaceholder = 'Search…',
      emptyText = 'No matches',
      invalid,
      disabled,
      size = 'md',
      id,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
    },
    ref,
  ) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listId = useId();

    const selected = options.find((o) => o.value === value && o.value !== '');
    const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    }, [options, query]);

    const reposition = useCallback(() => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
    }, []);

    useLayoutEffect(() => {
      if (open) reposition();
    }, [open, reposition]);

    useEffect(() => {
      if (!open) return;
      setQuery('');
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      const onMouseDown = (e: MouseEvent) => {
        const tgt = e.target as Node;
        if (!triggerRef.current?.contains(tgt) && !popoverRef.current?.contains(tgt))
          setOpen(false);
      };
      const onReflow = () => reposition();
      document.addEventListener('mousedown', onMouseDown);
      window.addEventListener('resize', onReflow);
      window.addEventListener('scroll', onReflow, true); // capture: catches scroll inside a Modal body
      return () => {
        clearTimeout(t);
        document.removeEventListener('mousedown', onMouseDown);
        window.removeEventListener('resize', onReflow);
        window.removeEventListener('scroll', onReflow, true);
      };
    }, [open, reposition]);

    const setTriggerRef = (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    const choose = (v: string) => {
      onChange(v);
      setOpen(false);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const o = filtered[active];
        if (o && !o.disabled) choose(o.value);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };

    return (
      <div className={cn('relative w-full', disabled && 'opacity-45')}>
        <button
          ref={setTriggerRef}
          type="button"
          id={id}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-invalid={invalid || undefined}
          aria-label={ariaLabel}
          data-testid={dataTestId}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={(e) => {
            // Focus moves to the search box a tick after opening. Until it does,
            // Escape must still close only this list, not a surrounding dialog.
            if (open && e.key === 'Escape') {
              e.preventDefault();
              setOpen(false);
            }
          }}
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-[10px] bg-surface text-start text-ink',
            'border border-hairline-2',
            'transition-[box-shadow,border-color] duration-[120ms] ease-[cubic-bezier(.4,0,.2,1)]',
            'focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-accent/25',
            'disabled:cursor-not-allowed',
            invalid && 'border-danger focus:border-danger focus:ring-danger/25',
            SIZE[size],
          )}
        >
          <span className={cn('truncate', !selected && 'text-ink-3')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDownIcon size={16} color="currentColor" />
        </button>

        {open
          ? createPortal(
              <div
                ref={popoverRef}
                data-overlay-popover=""
                style={{
                  position: 'fixed',
                  top: coords?.top ?? 0,
                  left: coords?.left ?? 0,
                  width: coords?.width ?? 'auto',
                  minWidth: 220,
                  visibility: coords ? 'visible' : 'hidden',
                }}
                className="z-[100] overflow-hidden rounded-[10px] border border-hairline-2 bg-surface shadow-lg"
              >
                <div className="border-b border-hairline p-2">
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setActive(0);
                    }}
                    onKeyDown={onKeyDown}
                    placeholder={searchPlaceholder}
                    role="combobox"
                    aria-controls={listId}
                    aria-expanded={open}
                    aria-autocomplete="list"
                    className={cn(
                      'h-9 w-full rounded-[8px] bg-bg px-2.5 text-[13px] text-ink',
                      'border border-hairline-2 focus:border-accent focus:outline-none',
                    )}
                  />
                </div>
                <ul id={listId} role="listbox" className="max-h-60 overflow-auto py-1">
                  {filtered.length === 0 ? (
                    <li className="px-3 py-2 text-[13px] text-ink-3">{emptyText}</li>
                  ) : (
                    filtered.map((o, i) => (
                      <li
                        key={o.value}
                        role="option"
                        aria-selected={o.value === value}
                        aria-disabled={o.disabled || undefined}
                        onMouseEnter={() => setActive(i)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (!o.disabled) choose(o.value);
                        }}
                        className={cn(
                          'cursor-pointer px-3 py-2 text-[13px]',
                          o.disabled && 'cursor-not-allowed opacity-40',
                          i === active ? 'bg-accent/10 text-ink' : 'text-ink-2',
                          o.value === value && 'font-semibold text-ink',
                        )}
                      >
                        {o.label}
                      </li>
                    ))
                  )}
                </ul>
              </div>,
              document.body,
            )
          : null}
      </div>
    );
  },
);
